import React, { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Paper,
	Typography,
	Stepper,
	Step,
	StepLabel,
	CircularProgress,
	Container,
	Button as MuiButton,
} from '@mui/material';
import { EmployeeSelfEntryForm } from './EmployeeSelfEntryForm';
import { OnboardingDocumentViewer } from './OnboardingDocumentViewer';
import {
	getOnboardingPhaseTwoStatus,
	getOnboardingById,
	getOnboardingDocuments,
	OnboardingPhaseTwoStatus,
	OnboardingRecord,
	submitOnboardingPhaseTwo,
} from 'core/API/onboarding';
import { IOnboardingDocument } from 'core/interfaces/onboardingInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import style from './EmployeeOnboardingProgress.module.scss';
import { useAuth } from 'module/auth/context/AuthContext';
// import { Button } from 'components/ui/button';

/**
 * EmployeeOnboardingProgress
 *
 * This component is displayed when a user logs in with isOnboardingInProgress = true
 * It shows a stepper UI to track onboarding progress and allows the employee to fill in their information
 *
 * Flow:
 * 1. Call GET api/onboarding-phase-two/status to get progress/status
 * 2. Use onboarding IRI from status to fetch full onboarding record via /api/onboardings/{id}
 * 3. Fetch document templates via api/onboarding_document_templates
 * 4. Display basic info form (editable based on status) and document steps
 */
export default function EmployeeOnboardingProgress(): JSX.Element {
	const { logout } = useAuth();
	const [isLoading, setIsLoading] = useState(true);
	const [phaseTwoStatus, setPhaseTwoStatus] = useState<OnboardingPhaseTwoStatus | null>(null);
	const [onboardingRecord, setOnboardingRecord] = useState<OnboardingRecord | null>(null);
	const [_documentTemplates, setDocumentTemplates] = useState<IOnboardingDocument[]>([]);
	const [documentTemplatesList, setDocumentTemplatesList] = useState<IOnboardingDocument[]>([]);
	const [activeStep, setActiveStep] = useState(0);
	const [workplace, setWorkplace] = useState<any | null>(null);
	const [isSubmittingPhaseTwo, setIsSubmittingPhaseTwo] = useState(false);
	const { showToast } = useToastContext();

	console.log('[EmployeeOnboardingProgress] Component rendered');

	const hasDocuments = documentTemplatesList.length > 0;
	const isSubmitted = !!onboardingRecord?.phase2CompletedAt;
	const steps = hasDocuments
		? ['Basic Information', 'Fill Documents', 'Review & Submit']
		: ['Basic Information', 'Submit', 'Complete'];

	// Check if submit button should be enabled
	const isPhase1Completed = phaseTwoStatus?.progress?.basic_info?.status === 'completed' ||
		phaseTwoStatus?.progress?.basic_info?.status === 'approved';
	const allDocumentsCompleted = hasDocuments
		? phaseTwoStatus?.progress?.documents?.every(
				(doc) => doc.status === 'completed' || doc.status === 'approved'
		  ) ?? false
		: true; // No documents means document requirement is satisfied
	const canSubmitPhaseTwo = isPhase1Completed && allDocumentsCompleted && !isSubmitted;

	// Navigation button states
	const canGoBack = activeStep > 0 && !isSubmitted;
	const canGoNext = !isSubmitted && (() => {
		if (activeStep === 0) {
			// Can move to step 1 if basic info is completed
			return isPhase1Completed;
		} else if (activeStep === 1) {
			// Can move to step 2 if all documents are completed (or no documents required)
			return allDocumentsCompleted;
		}
		return false; // Can't go forward from step 2
	})();

	/**
	 * Extract the onboarding ID from the IRI string (e.g., "/api/onboardings/1" -> 1)
	 */
	const extractIdFromIRI = (iri: string): number | null => {
		const match = iri.match(/\/(\d+)$/);
		return match ? parseInt(match[1], 10) : null;
	};

	const fetchOnboardingData = useCallback(async () => {
		setIsLoading(true);
		try {
			// Step 1: Get phase two status
			const status = await getOnboardingPhaseTwoStatus();
			if (!status) {
				showToast('error', 'Onboarding status not found');
				setIsLoading(false);
				return;
			}
			setPhaseTwoStatus(status);

			// Step 2: Extract onboarding ID from the status and fetch full onboarding record
			const onboardingId = extractIdFromIRI(status.onboarding);
			if (onboardingId) {
				const record = await getOnboardingById(onboardingId);
				if (record) {
					setOnboardingRecord(record);

					// Fetch workplace details from onboarding record
					const workplaceId = extractIdFromIRI(record.workplace);
					if (workplaceId) {
						try {
							const workplacesMap = JSON.parse(localStorage.getItem('workplaces-map') || '{}');
							const matched = workplacesMap[workplaceId.toString()];
							if (matched) setWorkplace(matched);
						} catch (err) {
							console.error('Error reading workplace from localStorage:', err);
						}
					}
				}
			}

			// Step 3: Fetch document templates
			const templates = await getOnboardingDocuments();
			setDocumentTemplates(templates);
			setDocumentTemplatesList(templates);

			// Determine active step based on status
			const basicInfoStatus = status.progress?.basic_info?.status;
			if (basicInfoStatus === 'completed' || basicInfoStatus === 'approved') {
				const hasDocumentTemplates = templates.length > 0;
				if (!hasDocumentTemplates) {
					// No documents — check if phase 2 was already submitted
					const record = onboardingId ? await getOnboardingById(onboardingId) : null;
					if (record?.phase2CompletedAt) {
						setActiveStep(2); // Already submitted, show complete
					} else {
						setActiveStep(1); // Show submit view
					}
				} else {
					// Basic info is done, check documents
					const allDocsCompleted =
						status.progress?.documents?.length > 0 &&
						status.progress.documents.every(
							(doc) => doc.status === 'completed' || doc.status === 'approved'
						);
					if (allDocsCompleted) {
						setActiveStep(2); // Complete
					} else {
						setActiveStep(1); // Documents
					}
				}
			} else {
				setActiveStep(0); // Basic Information
			}
		} catch (error) {
			console.error('Error fetching onboarding data:', error);
			showToast('error', 'Failed to load onboarding information');
		} finally {
			setIsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch onboarding data only once on component mount
	useEffect(() => {
		console.log('[EmployeeOnboardingProgress] Component mounted - fetching data');
		fetchOnboardingData();
		
		return () => {
			console.log('[EmployeeOnboardingProgress] Component unmounted');
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleFormSubmit = async () => {
		// Refresh the onboarding data after submission
		await fetchOnboardingData();
		showToast('success', 'Information saved successfully!');
	};

	const handleSubmitPhaseTwo = async () => {
		if (!onboardingRecord) return;
		setIsSubmittingPhaseTwo(true);
		try {
			await submitOnboardingPhaseTwo(onboardingRecord.id);
			showToast('success', 'Onboarding submitted for review!');
			await fetchOnboardingData();
		} catch (error) {
			console.error('Error submitting phase two:', error);
			showToast('error', 'Failed to submit onboarding. Please try again.');
		} finally {
			setIsSubmittingPhaseTwo(false);
		}
	};

	const handleBack = () => {
		if (canGoBack) {
			setActiveStep((prev) => prev - 1);
		}
	};

	const handleNext = () => {
		if (canGoNext && activeStep < steps.length - 1) {
			setActiveStep((prev) => prev + 1);
		}
	};

	if (isLoading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					backgroundColor: '#f5f5f5',
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!phaseTwoStatus) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					backgroundColor: '#f5f5f5',
				}}
			>
				<Paper sx={{ p: 4, maxWidth: 500 }}>
					<Typography variant="h6" sx={{ mb: 2 }}>
						Onboarding Record Not Found
					</Typography>
					<Typography variant="body1" color="text.secondary">
						We couldn&apos;t find your onboarding record. Please contact your manager or HR
						department.
					</Typography>
				</Paper>
			</Box>
		);
	}

	return (
		<div className={style.OnboardingProgressContainer}>
			<Container maxWidth="lg" sx={{ py: 4 }}>
				{/* Header */}
				<Paper sx={{ p: 3, mb: 4, backgroundColor: '#fff' }}>
					<Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
						Welcome to Your Onboarding Journey!
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
						Complete the following steps to finish your onboarding process.
					</Typography>

					{/* Stepper */}
					<Stepper activeStep={activeStep} alternativeLabel>
						{steps.map((label, index) => (
							<Step
								key={label}
								completed={index < activeStep || (isSubmitted && index === activeStep)}
								sx={{ cursor: index < activeStep && !isSubmitted ? 'pointer' : 'default' }}
								onClick={() => {
									if (index < activeStep && !isSubmitted) {
										setActiveStep(index);
									}
								}}
							>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
				</Paper>

				{/* Workplace Info */}
				{workplace && (
					<Paper sx={{ p: 3, mb: 3, backgroundColor: '#fff', border: '1px solid #e0e0e0' }}>
						<Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
							Onboarding Workplace
						</Typography>
								<Typography variant="h6" sx={{ fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
							{workplace.name}
						</Typography>
						{(workplace.streetAddress || workplace.state || workplace.country) && (
							<Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
								{[workplace.streetAddress, workplace.state, workplace.country]
									.filter(Boolean)
									.join(', ')}
							</Typography>
						)}
					</Paper>
				)}

				{/* Content */}
				{activeStep === 0 && onboardingRecord && (
					<EmployeeSelfEntryForm
						onboardingRecord={onboardingRecord}
						editable={true}
						onSubmit={handleFormSubmit}
					/>
				)}

				{activeStep === 1 && !hasDocuments && onboardingRecord && (
					<Paper sx={{ p: 4, textAlign: 'center' }}>
						<Typography variant="h6" sx={{ mb: 2, color: 'rgb(68, 99, 136)', fontWeight: 600 }}>
							No Documents Required
						</Typography>
						<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
							There are no documents to fill out for this phase. You can submit your
							onboarding for review.
						</Typography>
						<MuiButton
							variant="contained"
							onClick={handleSubmitPhaseTwo}
							disabled={isSubmittingPhaseTwo || !canSubmitPhaseTwo}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								px: 4,
								py: 1.5,
								'&:hover': {
									backgroundColor: 'rgb(58, 89, 126)',
								},
							'&.Mui-disabled': {
								backgroundColor: 'rgba(68, 99, 136, 0.5)',
								color: 'rgba(255, 255, 255, 0.7)',
							},
						}}
					>
						{isSubmittingPhaseTwo ? <CircularProgress size={24} color="inherit" /> : 'Submit for Review'}
					</MuiButton>
				</Paper>
			)}

			{activeStep === 1 && hasDocuments && phaseTwoStatus && onboardingRecord && (
				<OnboardingDocumentViewer
					documentStatuses={phaseTwoStatus.progress?.documents || []}
					documentTemplates={documentTemplatesList}
					onboardingId={onboardingRecord.id}
					onRefresh={fetchOnboardingData}
					onAllDocumentsComplete={() => setActiveStep(2)}
				/>
			)}

			{activeStep === 2 && isSubmitted && (
				<Paper sx={{ p: 4 }}>
						<Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
							Onboarding Complete!
						</Typography>
						<Typography variant="body1" color="text.secondary">
							You&apos;ve completed your onboarding process. Your manager will review your
							submission.
						</Typography>
					</Paper>
				)}

				{activeStep === 2 && !isSubmitted && hasDocuments && onboardingRecord && (
					<Paper sx={{ p: 4, textAlign: 'center' }}>
						<Typography variant="h6" sx={{ mb: 2, color: 'rgb(68, 99, 136)', fontWeight: 600 }}>
							All Documents Completed
						</Typography>
						<Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
							You&apos;ve filled out all required documents. If you need to make changes,
							click on a previous step above to go back and edit.
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
							Once submitted, you will not be able to make further changes.
						</Typography>
						<MuiButton
							variant="contained"
							onClick={handleSubmitPhaseTwo}
							disabled={isSubmittingPhaseTwo || !canSubmitPhaseTwo}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								px: 4,
								py: 1.5,
								'&:hover': {
									backgroundColor: 'rgb(58, 89, 126)',
								},
								'&.Mui-disabled': {
									backgroundColor: 'rgba(68, 99, 136, 0.5)',
									color: 'rgba(255, 255, 255, 0.7)',
								},
							}}
						>
							{isSubmittingPhaseTwo ? <CircularProgress size={24} color="inherit" /> : 'Submit for Review'}
						</MuiButton>
					</Paper>
				)}

				{activeStep === 2 && !isSubmitted && !hasDocuments && (
					<Paper sx={{ p: 4 }}>
						<Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
							Onboarding Complete!
						</Typography>
						<Typography variant="body1" color="text.secondary">
							You&apos;ve completed your onboarding process. Your manager will review your
							submission.
						</Typography>
					</Paper>
				)}

				{/* Navigation Buttons */}
				{!isSubmitted && (
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, mb: 2 }}>
						<MuiButton
							variant="outlined"
							onClick={handleBack}
							disabled={!canGoBack}
							sx={{
								borderColor: 'rgb(68, 99, 136)',
								color: 'rgb(68, 99, 136)',
								px: 4,
								py: 1.5,
								'&:hover': {
									borderColor: 'rgb(58, 89, 126)',
									backgroundColor: 'rgba(68, 99, 136, 0.04)',
								},
								'&.Mui-disabled': {
									borderColor: 'rgba(68, 99, 136, 0.3)',
									color: 'rgba(68, 99, 136, 0.5)',
								},
							}}
						>
							Back
						</MuiButton>
						<MuiButton
							variant="contained"
							onClick={handleNext}
							disabled={!canGoNext || activeStep >= steps.length - 1}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								px: 4,
								py: 1.5,
								'&:hover': {
									backgroundColor: 'rgb(58, 89, 126)',
								},
								'&.Mui-disabled': {
									backgroundColor: 'rgba(68, 99, 136, 0.5)',
									color: 'rgba(255, 255, 255, 0.7)',
								},
							}}
						>
							Next
						</MuiButton>
					</Box>
				)}

				{/* Logout Button */}
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
					<button
						onClick={(e) => {
							e.preventDefault();
							logout();
						}}
						style={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							padding: '10px 32px',
							border: 'none',
							borderRadius: '4px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: 500,
							transition: 'background-color 0.2s',
						}}
						onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'rgb(58, 89, 126)')}
						onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'rgb(68, 99, 136)')}
					>
						Logout
					</button>
				</Box>
			</Container>
		</div>
	);
}
